import React, { useEffect, useState } from "react";
import { X, Trophy, Clock } from "lucide-react";
import axios from "../../../routers/axios";
import { tokenManager } from "../../../routers/api";

const FinalResultsModal = ({
  showResultsModal,
  setShowResultsModal,
  navigate,
  testId,
  testName
}) => {
  const [reviewData, setReviewData] = useState(null);
  const [scores, setScores] = useState({
    reading1: 0,
    reading2: 0,
    writing1: 0,
    writing2: 0,
    total: 0,
  });

  useEffect(() => {
    const fetchReview = async () => {
      try {
        const user = tokenManager.getUser();
        if (!user || !testId || testId === "undefined") return;

        const res = await axios.get(
          `/test_attempts/review/user/${user.id}/test/${Number(testId)}`
        );
        console.log("[FinalResultsModal] review data:", res.data);
        setReviewData(res.data);

        // ✅ Lấy dữ liệu từ BE
        const s1 = res.data.scenarios.find(s => s.scenario_id === 1);
        const s2 = res.data.scenarios.find(s => s.scenario_id === 2);

        // ✅ Reading là điểm gốc (score)
        // ✅ Writing tạm chia nửa vì chưa có trường riêng
        setScores({
          reading1: s1?.score ?? 0,
          reading2: s2?.score ?? 0,
          writing1: Math.round((s1?.score ?? 0) / 2),
          writing2: Math.round((s2?.score ?? 0) / 2),
          total: res.data.total_score ?? 0,
        });
      } catch (err) {
        console.error("[FinalResultsModal] Lỗi khi fetch review:", err);
      }
    };

    if (showResultsModal && testId && testId !== "undefined") fetchReview();
  }, [showResultsModal, testId]);

  if (!showResultsModal) return null;

  const totalReading = (scores.reading1 || 0) + (scores.reading2 || 0);
  const totalWriting = (scores.writing1 || 0) + (scores.writing2 || 0);
  const totalScore = scores.total || 0;
  const percentage = ((totalScore / 30) * 100).toFixed(0);

  // Tính grade
  let grade = "F";
  if (percentage >= 85) grade = "A";
  else if (percentage >= 70) grade = "B";
  else if (percentage >= 55) grade = "C";
  else if (percentage >= 40) grade = "D";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="bg-blue-600 text-white rounded-t-2xl flex justify-between items-center px-6 py-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6" />
            <h2 className="text-lg font-semibold">Test Results</h2>
          </div>
          <button
            onClick={() => setShowResultsModal(false)}
            className="text-white hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-2xl font-bold text-gray-800 text-center mb-6">
            {testName || `Test ${testId}`}
          </h3>

          {/* Grade Section */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center mb-6">
            <div className="text-6xl font-extrabold text-green-600">{grade}</div>
            <div className="text-xl font-semibold mt-2">
              {totalScore}/30 points ({percentage}%)
            </div>
            <p className="text-green-700 font-medium mt-1">Excellent! 🎉</p>
          </div>

          {/* Reading & Writing */}
          <div className="grid grid-cols-2 gap-6">
            {/* Reading */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="text-blue-700 font-semibold flex items-center mb-2">
                <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center mr-2 text-sm">
                  R
                </span>
                Reading
              </h4>
              <p>Scenario 1: {scores.reading1}/15</p>
              <p>Scenario 2: {scores.reading2}/15</p>
              <p className="mt-2 font-semibold">
                Total Reading: {totalReading}/30
              </p>
            </div>

            {/* Writing */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <h4 className="text-green-700 font-semibold flex items-center mb-2">
                <span className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center mr-2 text-sm">
                  W
                </span>
                Writing
              </h4>
              <p>Scenario 1: {scores.writing1}</p>
              <p>Scenario 2: {scores.writing2}</p>
              <p className="mt-2 font-semibold">
                Total Writing: {totalWriting}/30
              </p>
            </div>
          </div>

          {/* Time Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mt-6">
            <h4 className="font-semibold text-gray-700 flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-500" /> Time Information
            </h4>
            <div className="grid grid-cols-2 gap-4 mt-3 text-sm text-gray-600">
              <div>
                <p>Scenario 1 Reading: N/A</p>
                <p>Scenario 2 Reading: N/A</p>
              </div>
              <div>
                <p>Scenario 1 Writing: N/A</p>
                <p>Scenario 2 Writing: N/A</p>
              </div>
            </div>
          </div>

          {/* Back Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => {
                setShowResultsModal(false);
                navigate("/student/dashboard");
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalResultsModal;
